// 剣舞が技の戻りの後に後ろへ下がる所 (2026-11-04)。配布版の既定は 'lock' (11-04 作者 backstep=lock「ちょっと気になる気もするけど一旦これで進めて」)。
//   前の版 (立ち姿のまま滑る) は DEV `?bladeBackStep=now`、歩きの2枚の比較版は `?bladeBackStep=walk`。
//   見つけた所: 3ボスの壊れ探し (docs/research/2026-11-04-three-bosses-break-hunt.md 問1) で、間合いの立て直し
//   (src/boss.ts の regainActive) の約 90px を、待機の絵 (m1025_w_1) のまま約 170ms で滑って下がっていた (505 秒で6回)。
//   原因: 歩きの絵へ移る門 (150ms の遷移待ち・1サイクル 130px の下限) が 90px の短い下がりでは開かない。
//   'walk' = 下がる間だけ門を通さず、ワルツの2枚 (w_1 構え → w_2 踏み → w_1) を「今回の移動量」で1歩ぶん踏み替える。
//   2枚の踏み替えなので「逆に回す」と並びは同じ (覚醒王の後ろ歩きと同じ事情)。踏み出しが w_1 から始まるよう
//   これまでの総距離 (walkDist) でなく今回の移動量 (walkBurstPx) で回し、溶かさずパッと切り替える (2枚の輪郭が違うため)。
//   'lock' (11-04 作者「歩幅と移動距離が一致してないように見える」への直し) = 足を床に留めた1歩。体は絵が替わる瞬間だけ動く:
//     ①構え w_1 (爪先1本で立つ) → 踏み w_2: 前の爪先を床の同じ点に残したまま後ろ足を伸ばす = 体は BACK_STEP_REACH_PX 下がる
//     ②踏み w_2 → 構え w_1: 伸ばした後ろ足の爪先に立つ = 体はさらに BACK_STEP_LAND_PX 下がる。1歩 = 約 151px (絵で決まる)。
//     立て直しで要る距離 (36〜99px) より長いので、下がった後は次の技まで詰める歩きをせず、その場から技を出す
//     (詰める歩きは 130px 未満だと立ち絵のまま滑るため)。後ろの壁で1歩が入らない時は下がらない (半端に動かすと足が滑る)。
//   正本 = docs/research/2026-11-04-blade-backstep-compare.md・docs/research/2026-11-04-blade-backstep-lock.md
import type { Boss } from './types'

export type BladeBackStepMode = 'now' | 'walk' | 'lock'
let mode: BladeBackStepMode = 'lock'
/** DEV の切り替え (game.ts)。'walk' / 'now' だけが比較版。 */
export function setBladeBackStep(v: string | null): BladeBackStepMode {
  mode = import.meta.env.DEV && (v === 'walk' || v === 'now') ? v : 'lock'
  return mode
}
export function getBladeBackStep(): BladeBackStepMode { return mode }

/** 1歩 (構え → 踏み → 構え) にかける移動量。立て直しの下がり (約 90px) でちょうど1歩。 */
export const BLADE_BACK_STEP_STRIDE_PX = 90

/** 比較版で、剣舞が立て直しで後ろへ下がっている最中か。 */
export function isBladeBackStepWalk(boss: Boss): boolean {
  return mode === 'walk' && boss.id === 'blade-dancer' && boss.state === 'idle' && boss.hp > 0
    && (boss.regainActive ?? false)
}

/** 今回の移動量から、どちらの絵か (false = 構え w_1・true = 踏み w_2)。真ん中の半分だけ踏み。 */
export function bladeBackStepIsStep(burstPx: number): boolean {
  const t = ((burstPx / BLADE_BACK_STEP_STRIDE_PX) % 1 + 1) % 1
  return t >= 0.25 && t < 0.75
}

// ---- 'lock' (足を床に留めた1歩) ----
// 床に着く爪先の位置 (体の前方 +・ゲーム px・PNG の一番下の不透明画素)。tests/blade_back_step.test.ts が PNG と描く位置から測り直して照合する。
//   w_1 = 爪先 +16.0 (もう片方の爪先は床から 6px 浮く)・w_2 = 前の爪先 +55.0 / 後ろの爪先 -96.2 (床から 5px)。
export const BLADE_BACK_STEP_FOOT_PX = { hold: 16.0, stepFront: 55.0, stepBack: -96.2 } as const
/** ①の下がり = 踏みの前の爪先 − 構えの爪先。 */
export const BLADE_BACK_STEP_REACH_PX = BLADE_BACK_STEP_FOOT_PX.stepFront - BLADE_BACK_STEP_FOOT_PX.hold
/** ②の下がり = 構えの爪先 − 踏みの後ろの爪先。 */
export const BLADE_BACK_STEP_LAND_PX = BLADE_BACK_STEP_FOOT_PX.hold - BLADE_BACK_STEP_FOOT_PX.stepBack
export const BLADE_BACK_STEP_PX = BLADE_BACK_STEP_REACH_PX + BLADE_BACK_STEP_LAND_PX
/** 踏み w_2 を見せる長さと、構えに立ってから技に入れるまでの長さ。1歩 0.3 秒 ≒ 今の立て直しの速さ (約 500px/s)。 */
export const BLADE_BACK_STEP_REACH_MS = 180
export const BLADE_BACK_STEP_LAND_MS = 120
/** 下がった後、その場から技を出してよい距離の上乗せ (立て直しの帯 43px + 1歩 151px = 194px が技の間合い 185px を少し越えるため)。 */
export const BLADE_BACK_STEP_COMMIT_BONUS_PX = 15

/** 'lock' の動きを boss.ts で受け持つか (1歩の最中・立て直しで下がりたい時・下がった後の待機)。 */
export function bladeBackStepLockOwns(boss: Boss, wantsBack: boolean): boolean {
  return mode === 'lock' && boss.id === 'blade-dancer' && boss.state === 'idle' && boss.hp > 0
    && (boss.bladeBackStep !== undefined || ((boss.regainActive ?? false) && wantsBack)
      || boss.bladeBackStepAfter === boss.attackCount)
}

/** 下がった後の待機 (次の技まで) か。 */
export function bladeBackStepSettled(boss: Boss): boolean {
  return mode === 'lock' && boss.id === 'blade-dancer' && boss.bladeBackStepAfter === boss.attackCount
}

/**
 * 1 tick 進める。体 (x) は段の始まりの tick にだけ動く。
 * blocksAttack = 1歩の最中は技に入らない。
 */
export function advanceBladeBackStep(
  boss: Boss, _playerX: number, wantsBack: boolean, bounds: { leftWall: number; rightWall: number }, dtMs: number,
): { boss: Boss; blocksAttack: boolean } {
  const step = boss.bladeBackStep
  if (!step) {
    if (!(boss.regainActive ?? false) || !wantsBack) return { boss, blocksAttack: false }
    // 下がる向きは今向いている向きの後ろ (主人公の位置で決めると、すり抜けの真上で向きと逆へ踏み、絵が左右に返って爪先がずれた・11-04 撮影)。
    //   1歩の間は向きを変えない (boss.ts の reFaceNow)。
    const away: 1 | -1 = boss.facing === 'left' ? 1 : -1
    const end = boss.x + away * BLADE_BACK_STEP_PX
    // 壁で1歩が入らない時は下がらず、立て直しを終える。
    if (end < bounds.leftWall || end > bounds.rightWall) {
      return { boss: { ...boss, regainActive: false, regainDeferredMs: 0 }, blocksAttack: false }
    }
    return { boss: { ...boss, x: boss.x + away * BLADE_BACK_STEP_REACH_PX, bladeBackStep: { phase: 'reach', ms: 0, away } }, blocksAttack: true }
  }
  const ms = step.ms + dtMs
  if (step.phase === 'reach') {
    if (ms < BLADE_BACK_STEP_REACH_MS) return { boss: { ...boss, bladeBackStep: { ...step, ms } }, blocksAttack: true }
    return { boss: { ...boss, x: boss.x + step.away * BLADE_BACK_STEP_LAND_PX, bladeBackStep: { phase: 'land', ms: 0, away: step.away } }, blocksAttack: true }
  }
  if (ms < BLADE_BACK_STEP_LAND_MS) return { boss: { ...boss, bladeBackStep: { ...step, ms } }, blocksAttack: true }
  const { bladeBackStep: _s, ...rest } = boss
  return { boss: { ...rest, regainActive: false, regainDeferredMs: 0, bladeBackStepAfter: boss.attackCount }, blocksAttack: false }
}

/** 絵 (sprites.ts): 1歩の最中の絵。null = 関わらない。 */
export function bladeBackStepLockPose(boss: Boss): 'hold' | 'step' | null {
  if (mode !== 'lock' || boss.id !== 'blade-dancer' || boss.state !== 'idle') return null
  const step = boss.bladeBackStep
  if (!step) return null
  return step.phase === 'reach' ? 'step' : 'hold'
}
